import { css, html, LitElement } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { Store } from "../../state/store";
import { Controller } from "../../state/controller";
import { sharedStyles } from "../../styles/shared";
import { appStore } from "../../state/app/app-store";
import { property } from "lit/decorators/property.js";
import { classMap } from "lit/directives/class-map.js";
import { addRouteChangedListener, pushState, removeRouteChangedListener } from "../../util/route-util";
import { ROUTES } from "../../config";
import { when } from "lit/directives/when.js";
import { PropertyValues } from "lit/development";
import { openUserAuth } from "../../dialogs/open-user-auth";
import { DEFAULT_AUTH_PROVIDER_KINDS, EVENT_USER_SIGN_IN } from "shared";
import "../../atoms/logo";
import "../../atoms/title";
import "../../atoms/card";
import "../../atoms/text";
import "../../atoms/form-item";
import { setIsMenuOpen } from "../../state/app/app-action";
import { ICON_CLOSE, ICON_MENU } from "../../../icons";
import { themeStore } from "../../state/theme/theme-store";
import { eventService } from "../../services/event-service";
import { getStarted } from "../../util/landing-util";
import { sessionStore } from "../../state/session/session-store";

const STICKY_NAVBAR_TRIGGER = 60;

class LandingNavbarStore extends Store {
	model$ = {
		isMobile: appStore.model$.isMobile,
		isMenuOpen: appStore.model$.isMenuOpen,
		colorMode: themeStore.model$.colorMode,
		sessionUser: sessionStore.model$.sessionUser
	};
}

@customElement("bs-landing-navbar")
export class BsLandingNavbar extends LitElement {
	static get styles() {
		return [
			sharedStyles,
			css`
				#nav {
					position: fixed;
					top: 0;
					left: 0;
					width: 100%;
					z-index: 12345678;
					padding: 0 var(--space-l);
				}

				:host([dark][sticky]) #nav-container,
				:host([dark]) #mobile-menu {
					color: var(--light);
					background: rgba(12, 12, 12, 0.85);
				}

				:host([sticky][dark]) #nav-container {
					border-color: var(--shade-dark);
				}

				:host([dark]) bs-form-item[flat] {
					--form-item-bg: var(--light);
				}

				:host([dark]) #cta {
					--form-item-color: var(--primary-contrast-normal);
					--form-item-bg: var(--primary-normal);
				}

				:host([sticky]) #nav-container {
					max-width: 1350px;
					transform: translateY(10px);
					/*background: var(--background);*/
					background: rgba(255, 255, 255, 0.9);
					backdrop-filter: blur(10px);
					-webkit-backdrop-filter: blur(10px);
					border: 1px solid var(--shade-normal);
					box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
				}

				#nav-container {
					padding: var(--space-m) var(--space-l);
					margin: 0 auto;
					max-width: 1400px;
					width: 100%;
					display: flex;
					justify-content: space-between;
					align-items: center;
					transition: transform 120ms ease-out, max-width 120ms ease-out, border 120ms ease-out,
						box-shadow 120ms ease-out, background-color 120ms ease-out, color 120ms ease-out;
					border-radius: var(--border-radius-l);
					position: relative;
				}

				#nav-container > * {
					display: flex;
					align-items: center;
					gap: var(--space-m);
				}

				.active {
					cursor: default;
					font-weight: 800;
				}

				#mobile-menu {
					position: absolute;
					width: 100%;
					left: 0;
					top: calc(100% + var(--space-m));
					height: 60vh;
					z-index: 1234567;
					animation: move-in 120ms ease-out;
					animation-fill-mode: forwards;
				}

				#mobile-menu #menu {
					display: flex;
					flex-direction: column;
					--form-item-justify-content: flex-start;
					--form-item-button-transform-hover: none;
					gap: var(--space-s);
				}

				#mobile-menu > * {
					width: 100%;
				}

				@keyframes move-in {
					from {
						opacity: 0;
						transform: translateY(-20px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}

				@media (max-width: 800px) {
					#nav {
						padding: 0 var(--space-m);
					}

					#nav-container {
						padding: var(--space-m);
					}
				}
			`
		];
	}

	protected controller = new Controller(this, new LandingNavbarStore(), {
		colorMode: this.updateColorMode.bind(this)
	});

	@property({ type: Boolean, reflect: true }) sticky = false;
	@property({ type: Boolean, reflect: true }) dark = false;

	protected boundUpdateStickyNavbar = this.updateStickyNavbar.bind(this);
	protected boundRouteChanged = this.routeChanged.bind(this);

	protected firstUpdated(props: PropertyValues) {
		super.firstUpdated(props);
		window.addEventListener("scroll", this.boundUpdateStickyNavbar);
		addRouteChangedListener(this.boundRouteChanged);
		this.updateColorMode();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		removeRouteChangedListener(this.boundRouteChanged);
	}

	updateColorMode() {
		const { colorMode } = this.controller.model;
		this.dark = colorMode === "dark";
	}

	updateStickyNavbar() {
		const sticky = window.scrollY > STICKY_NAVBAR_TRIGGER;
		if (this.sticky !== sticky) {
			this.sticky = sticky;
		}
	}

	routeChanged() {
		this.requestUpdate();
	}

	async signIn() {
		if (
			await openUserAuth({
				headline: "Sign in",
				text: "Choose how you want to continue",
				kinds: DEFAULT_AUTH_PROVIDER_KINDS
			})
		) {
			eventService.trackEvent(EVENT_USER_SIGN_IN);
			pushState(ROUTES.home, { replace: true });
		}
	}

	getStarted() {
		getStarted("navbar");
	}

	launchApp() {
		window.open(ROUTES.home, "_blank");
	}

	toggleMenu() {
		const { isMenuOpen } = this.controller.model;
		setIsMenuOpen(!isMenuOpen);
	}

	renderMenu() {
		const { pathname } = location;
		return html`
			<div id="menu">
				<bs-form-item class="menu-item" invert flat>
					<button
						slot="input"
						@click="${() => pushState(ROUTES.features())}"
						class="${classMap({ active: pathname.startsWith(ROUTES.features()) })}"
					>
						Features
					</button>
				</bs-form-item>
				<bs-form-item class="menu-item" invert flat>
					<button
						slot="input"
						@click="${() => pushState(ROUTES.pricing)}"
						class="${classMap({ active: pathname.startsWith(ROUTES.pricing) })}"
					>
						Pricing
					</button>
				</bs-form-item>
				<bs-form-item class="menu-item" invert flat>
					<button
						slot="input"
						@click="${() => pushState(ROUTES.help())}"
						class="${classMap({ active: pathname.startsWith(ROUTES.help()) })}"
					>
						Help Center
					</button>
				</bs-form-item>
				<bs-form-item class="menu-item" invert flat>
					<button
						slot="input"
						@click="${() => pushState(ROUTES.blog())}"
						class="${classMap({ active: pathname.startsWith(ROUTES.blog()) })}"
					>
						Blog
					</button>
				</bs-form-item>
			</div>
		`;
	}

	renderMobileMenu() {
		return html`
			<bs-card id="mobile-menu">
				<article>${this.renderMenu()}</article>
				<footer>${this.renderCta()}</footer>
			</bs-card>
		`;
	}

	renderCta() {
		if (this.controller.model.sessionUser != null) {
			import("../../molecules/session-avatar");
			return html`
				<bs-session-avatar hide-name></bs-session-avatar>
				<bs-form-item id="cta">
					<button slot="input" @click="${this.launchApp}">Launch Ideamap</button>
				</bs-form-item>
			`;
		}

		return html`
			<bs-form-item invert flat>
				<button slot="input" @click="${this.signIn}">Sign In</button>
			</bs-form-item>
			<bs-form-item id="cta">
				<button slot="input" @click="${this.getStarted}">Get Started</button>
			</bs-form-item>
		`;
	}

	render() {
		const { isMobile, isMenuOpen } = this.controller.model;
		return html`
			<nav id="nav" class="${classMap({ sticky: this.sticky })}">
				<div id="nav-container">
					<div>
						<bs-logo id="logo" ?dark="${this.dark}"></bs-logo>
						${when(!isMobile, () => this.renderMenu())}
					</div>
					<div>
						${when(
							isMobile,
							() =>
								html`
									<bs-icon @click="${this.toggleMenu}" button .icon="${isMenuOpen ? ICON_CLOSE : ICON_MENU}"></bs-icon>
								`,
							() => this.renderCta()
						)}
					</div>
					${when(isMobile && isMenuOpen, () => this.renderMobileMenu())}
				</div>
			</nav>
		`;
	}
}
